/* CreateAction.js */

"use strict";

const _ = require("lodash");
const tus = require("tus-js-client");

const CreateFileDetailsComponent = require("./CreateFileDetailsComponent");
const CreateFileUploadComponent = require("./CreateFileUploadComponent");
const {
    getFileType,
    OS_IMAGE_STATUS,
    isDraft,
    isUploading,
    isUploaded,
    isComplete,
} = require("../../fileConstants");

const UPLOAD_ENDPOINT = (id) => `/gfs/uploads/${id}`;
const LOADING_MSG = (isSingle) =>
    `Processing your ${isSingle ? "file" : "files"}... This could take a moment`;

const UPLOAD_RESULTS = ({totalUploads, successful, failed, aborted}) => `
            Total number of files: ${totalUploads}
            Successful: ${successful}
            Failed: ${failed}
            ${aborted ? `Aborted: ${aborted}` : ""}
        `;
const FULL_SUCCESSFUL_MSG = (total, name) =>
    total === 1 ? `File "${name}"` : `All ${total} files successfully uploaded`;
const FULL_FAILED_MSG = (total, msg) =>
    total === 1 ? `${msg}` : `All ${total} files failed to upload: ${msg}`;

const LOGOUT_CONFIRMATION_MSG =
    "File uploads are still in progress. Are you sure you want to sign out? All uploads will be canceled.";

class CreateAction {
    constructor(
        activeView,
        userService,
        userSession,
        controlAppNav,
        modalUserFeedback,
        activityMonitor,
        OsImageEntity,
        OsImageCatalogEntity,
        OsImageServerEntity,
        storageFactory,
        logger,
        osUpgradeInfoEntity
    ) {
        this._activeView = activeView;
        this._userService = userService;
        this._userSession = userSession;
        this._controlAppNav = controlAppNav;
        this._modalUserFeedback = modalUserFeedback;
        this._activityMonitor = activityMonitor;
        this._OsImageEntity = OsImageEntity;
        this._OsImageCatalogEntity = OsImageCatalogEntity;
        this._OsImageServerEntity = OsImageServerEntity;
        this._osUpgradeInfoEntity = osUpgradeInfoEntity;
        this._storageFactory = storageFactory;
        this._logger = logger;

        this._activeOrgId = _.get(this._userSession, ["activeOrganization", "id"]);
        this._currentPathId = this._activeView.getSelectedPath();

        /**
         * @type {{osImage: OsImageEntity, fileUploader: Upload}[]}
         * @private
         */
        this._data = [];

        this.shutdown = this.shutdown.bind(this);
        this.run = this.run.bind(this);
        this._bulkUpdate = this._bulkUpdate.bind(this, logger, this._activeOrgId, OsImageEntity);
        //this._bulkDelete = this._bulkDelete.bind(this, logger, this._activeOrgId, OsImageEntity);
        this._logError = this._logError.bind(this);
        this._abortUpload = this._abortUpload.bind(this);

        this._onWindowBeforeunload = this._onWindowBeforeunload.bind(this);
        this._onWindowUnload = this._onWindowUnload.bind(this);

        this._registerListeners();
    }

    /****************************/
    /* Start Private Component methods */
    /****************************/
    _registerListeners() {
        this._userSession.on("OrganizationChange", this.shutdown);
        this._activeView.on("Shutdown", this.shutdown);
        this._userSession.on("BeforeLogout", this.shutdown);
    }
    _registerWindowListeners() {
        window.addEventListener("beforeunload", this._onWindowBeforeunload);
        window.addEventListener("unload", this._onWindowUnload);
    }

    _unregisterListeners() {
        if (this._userSession) {
            this._userSession.removeListener("OrganizationChange", this.shutdown);
            this._userSession.removeListener("BeforeLogout", this.shutdown);
        }

        if (this._activeView) {
            this._activeView.removeListener("Shutdown", this.shutdown);
        }
    }
    _unregisterWindowListeners() {
        window.removeEventListener("beforeunload", this._onWindowBeforeunload);
        window.removeEventListener("unload", this._onWindowUnload);
    }
    /**********************************/
    /* End Private Component methods  */
    /* Start Public Component methods */
    /**********************************/

    run() {
        let aborted = false;
        if (!this._activeView.canCreateFiles()) {
            return Promise.reject("Can not upload files");
        }
        if (!this._currentPathId) {
            return Promise.reject("No selected folder");
        }
        return Promise.all([
            this._showGetFilesVeil(),
            this.getOsImageMasterServer(),
            this._storageFactory.whenStorageIsReady(),
        ])
            .then(([files, masterServerId]) => {
                this._userStorageData = this._storageFactory.getRemoteStorageInstance(
                    "file_grid_gl",
                    "upload_preset"
                );
                return Promise.all([
                    this._parseFilesPromise(files, masterServerId),
                    this._showLoadingVeil(LOADING_MSG(files.length === 1)),
                ]);
            })
            .then(([uploadingData]) => {
                this._data = uploadingData;
                return this._showFileDetailsVeil(
                    _.map(this._data, "osImage"),
                    this._userStorageData.getData()
                );
            })
            .then((metaData) => {
                const osImages = _.map(this._data, "osImage");

                if (osImages.length === 1 && osImages[0].getName() !== metaData.name) {
                    osImages[0].setName(metaData.name.trim()).update();
                }
                const osImageUpdateProps = {
                    vendor: metaData.vendor,
                    imageType: metaData.imageType,
                    status: OS_IMAGE_STATUS.PENDING_UPLOAD,
                };

                this._userStorageData.setData(metaData.vendor, "vendor");
                this._userStorageData.setData(metaData.imageType, "imageType");
                this._userStorageData.setData(metaData.imageCatalog, "imageCatalog");

                return Promise.all([
                    this.getOsImageCatalog(metaData.imageCatalog),
                    this._bulkUpdate(osImages, osImageUpdateProps),
                    this._showLoadingVeil(LOADING_MSG(this._data.length === 1)),
                ]);
            })
            .then((/*[osImageCatalog]*/) => {
                this._modalUserFeedback.displayLoading({
                    message: "Uploading files...",
                    showHideButton: true,
                    showAbortButton: true,
                    onAbort: () => {
                        aborted = true;
                        this._abortUpload();
                        this._showResultsVeil();
                    },
                });
                return this._startUploadQueue(this._data).then((data) => {
                    if (aborted || !this._modalUserFeedback.isStillOpen) {
                        return;
                    }
                    if (data && data.uploadComplete) {
                        return this._showResultsVeil();
                    }
                });
            })
            .catch((error) => {
                if (aborted) {
                    return;
                }
                if (error && !error.userRejected) {
                    this._logError("run", `main run error`)(error);
                    return this._modalUserFeedback.displayError(error);
                }
            });
    }

    shutdown() {
        this._cleanUp();
        this._unregisterListeners();
        _.invoke(this._modalUserFeedback, "hideDisplayed");

        this._activeView = null;
        this._userService = null;
        this._userSession = null;
        this._modalUserFeedback = null;
        this._OsImageEntity = null;
        this._OsImageCatalogEntity = null;
        this._OsImageServerEntity = null;
        this._osUpgradeInfoEntity = null;

        //this._data = null;
    }
    /********************************/
    /* End Public Component methods */
    /********************************/
    getOsImageCatalog(catalogId) {
        const orgId = this._activeOrgId;
        const loadCatalog = catalogId
            ? this._OsImageCatalogEntity.loadOsImageCatalog(orgId, catalogId, {id: true})
            : Promise.resolve();
        return loadCatalog
            .catch(this._logError("getOsImageCatalog"))
            .then((osImageCatalog) => (this._osImageCatalog = osImageCatalog));
    }

    getOsImageMasterServer() {
        const orgId = this._activeOrgId;
        return this._OsImageServerEntity
            .loadOsImageServers(orgId, {isMain: true}, {id: true})
            .then((servers) => servers[0] && servers[0].id)
            .catch(this._logError("getOsImageMasterServer"));
    }

    /**
     * Get not completed osImages abort and delete them.
     * @private
     */
    _abortUpload() {
        _.toArray(this._data)
            .filter(({osImage}) => !isComplete(osImage.status))
            .forEach(({fileUploader, osImage}, idx) => {
                if (isUploading(osImage.status) || idx === 0) {
                    fileUploader.abort(true);
                }
                osImage.setProperty("status", OS_IMAGE_STATUS.ABORTED);
                osImage
                    .delete()
                    .catch(this._logError("_abortUpload", `Error delete osImage ${osImage.name}`));
            });
    }

    /**
     * Clean up DRAFT entities
     * @private
     */
    _cleanUp() {
        _.toArray(this._data)
            .filter(({osImage}) => isDraft(osImage.status))
            .forEach(({osImage}) =>
                osImage
                    .delete()
                    .catch(this._logError("_cleanUp", `Error delete osImage ${osImage.name}`))
            );
    }

    /****************************/
    /* Start Show Veils section */
    /****************************/
    /**
     * @param {string} message
     * @return {Promise<undefined>}
     * @private
     */
    _showLoadingVeil(message) {
        return this._modalUserFeedback.displayLoading({message, dontHide: true});
    }

    /**
     *
     * @return {Promise<FileList>}
     * @private
     */
    _showGetFilesVeil() {
        return this._modalUserFeedback.displayCustom(new CreateFileUploadComponent(this._logger), {
            dontHide: true,
        });
    }
    /**
     * @param {OsImageEntity[]} osImages
     * @param {Object} initialData initial data for the selections
     * @return {Promise<{name: ?string, vendor: ?string, imageType: ?string, imageCatalog: ?string}>}
     * @private
     */
    _showFileDetailsVeil(osImages, initialData) {
        return this._modalUserFeedback.displayCustom(
            new CreateFileDetailsComponent(
                this._logger,
                this._userSession,
                osImages,
                this._OsImageCatalogEntity,
                this._userService,
                initialData,
                this._osUpgradeInfoEntity
            ),
            {dontHide: true}
        );
    }

    _showResultsVeil() {
        const totalUploads = this._data.length;
        const {successful, failed, aborted} = this._data.reduce(
            (results, {osImage /*, fileUploader*/}) => {
                results.aborted += osImage.status === OS_IMAGE_STATUS.ABORTED;
                results.successful += isUploaded(osImage.status);
                results.failed += osImage.status === OS_IMAGE_STATUS.FAILED;
                return results;
            },
            {successful: 0, failed: 0, aborted: 0}
        );

        let title = aborted ? "Upload Aborted" : "Upload Completed";
        let message = UPLOAD_RESULTS({totalUploads, successful, failed, aborted});

        if (successful === totalUploads) {
            message = FULL_SUCCESSFUL_MSG(totalUploads, this._data[0].osImage.name);
        }
        if (failed === totalUploads) {
            title = "Upload Failed";
            message = FULL_FAILED_MSG(
                totalUploads,
                this._data[0].osImage.errorMsg || "File " + this._data[0].osImage.name
            );
        }

        if (this._modalUserFeedback) {
            if (!successful) {
                return this._modalUserFeedback.displayError({
                    errorMsg: message,
                    errorCode: "",
                    title,
                });
            }
            return this._modalUserFeedback.displayNotification(message, {title});
        }
    }
    /****************************/
    /* End Show Veils section   */
    /****************************/
    /**
     * @param {FileList} files
     * @param {string} masterServerId
     * @return {Promise<{osImage: OsImageEntity, fileUploader: Upload}[]>}
     * @private
     */
    _parseFilesPromise(files, masterServerId) {
        const createDataItem = (file) =>
            this._createOsImage(file).then((osImage) => ({
                osImage,
                fileUploader: this._createPromisifiedUploader(file, osImage, masterServerId),
            }));
        return Promise.all(_.map(files, createDataItem));
    }

    /**
     *
     * @param name
     * @param type
     * @param size
     * @return {Promise<OsImageEntity|undefined>}
     * @private
     */
    _createOsImage({name, type, size}) {
        const fileName = name,
            fileExtension = fileName.includes(".") ? fileName.split(".").pop() : "";

        const osImage = new this._OsImageEntity(this._activeOrgId);

        osImage.setName(fileName);
        osImage.setPathId(this._currentPathId);
        osImage.setFileType(getFileType(fileExtension, type) || fileExtension.toUpperCase());
        osImage.setVendor(null);
        osImage.setImageType(null);

        osImage.setProperty("fileSize", size);
        osImage.setProperty("status", OS_IMAGE_STATUS.DRAFT);

        return osImage.createStatic().catch((error) => {
            this._logError("_createOsImage", `error osImage createStatic ${osImage.name}`)(error);
            osImage.errorMsg = error.errorMsg;
            return osImage;
        });
    }

    _createPromisifiedUploader(file, osImage, masterServerId) {
        let uploadResolve;
        let uploadReject;
        let onUploadProgress = () => {};
        let onChunkComplete = () => {};

        const uploadPromise = new Promise((resolve, reject) => {
            uploadResolve = resolve;
            uploadReject = reject;
        });

        let upload = new tus.Upload(file, {
            endpoint: UPLOAD_ENDPOINT(osImage.id),
            retryDelays: [1e2, 8e2, 27e2, 64e2, 125e2],
            metadata: {
                organizationId: this._activeOrgId,
                masterId: masterServerId,
                filename: osImage.name,
                fileType: osImage.imageType,
            },
            chunkSize: 3e6,
            resume: false,
            onError: function (error) {
                uploadReject(error);
            },
            onProgress: function (bytesUploaded, bytesTotal) {
                onUploadProgress(bytesUploaded, bytesTotal);
            },
            onChunkComplete: function (chunkSize, bytesAccepted, bytesTotal) {
                onChunkComplete(chunkSize, bytesAccepted, bytesTotal);
            },
            onSuccess: function () {
                uploadResolve(osImage);
            },
        });

        upload.start = (onProgress, onChunk) => {
            onUploadProgress = onProgress || onUploadProgress;
            onChunkComplete = onChunk || onChunkComplete;
            tus.Upload.prototype.start.call(upload);
            return uploadPromise;
        };
        upload.abort = (...args) => {
            tus.Upload.prototype.abort.apply(upload, args);
            uploadReject({aborted: true});
        };
        return upload;
    }

    _startUploadQueue(data) {
        this._preUpload();
        const updateStatus = (osImage, status) =>
            osImage
                .setProperty("status", status)
                .update()
                .catch(
                    this._logError(
                        "_startUploadQueue",
                        `Error osImage ${osImage.name} update to ${status} status`
                    )
                );

        const timeout = (...args) => new Promise((resolve) => setTimeout(resolve, 1e2, ...args));

        const fileUploadPromise = (fileUploader, osImage, idx) => {
            const onChunkUploadComplete_throttled = _.throttle(
                this._onChunkUploadComplete.bind(this, osImage),
                1e3,
                {
                    leading: true,
                    trailing: true,
                }
            );
            if (!osImage.existsInSystem()) {
                osImage.setProperty("status", OS_IMAGE_STATUS.FAILED);
                return;
            }
            return updateStatus(osImage, OS_IMAGE_STATUS.UPLOADING)
                .then(() =>
                    fileUploader.start(
                        this._onFileUploadProgress.bind(this, idx),
                        onChunkUploadComplete_throttled
                    )
                )
                .then(() => {
                    onChunkUploadComplete_throttled.flush();
                    onChunkUploadComplete_throttled.cancel();
                    return this._onFileUploadSuccess(this._osImageCatalog, osImage);
                })
                .catch((error) => {
                    onChunkUploadComplete_throttled.flush();
                    onChunkUploadComplete_throttled.cancel();
                    if (error && error.aborted) {
                        return Promise.reject(error);
                    }
                    return this._onFileUploadError(osImage, error);
                });
        };

        return data
            .reduce((uploadingQueuePromise, {fileUploader, osImage}, idx) => {
                osImage.setProperty("status", OS_IMAGE_STATUS.PENDING_UPLOAD);
                return uploadingQueuePromise
                    .then(timeout)
                    .then(() => fileUploadPromise(fileUploader, osImage, idx));
            }, Promise.resolve())
            .then(() => {
                this._postUpload();
                return {uploadComplete: true};
            })
            .catch((error) => {
                this._postUpload();
                return Promise.reject(error);
            });
    }

    _bulkUpdate(logger, tenantId, OsImageEntity, osImages, updateProps) {
        const generateOperations = (osImage) => ({
            actionType: "entity",
            action: "update",
            entityType: OsImageEntity.getEntityType(),
            entityId: osImage.id,
            properties: updateProps,
        });
        const imagesToBeUpdated = osImages.filter((osImage) => osImage.existsInSystem());
        return OsImageEntity.doBulk(tenantId, imagesToBeUpdated.map(generateOperations)).catch(
            this._logError("_bulkUpdate")
        );
    }

    /*
        _bulkDelete(logger, tenantId, OsImageEntity, osImages) {
            const generateOperations = (osImage) => ({
                actionType: "entity",
                action: "delete",
                entityType: OsImageEntity.getEntityType(),
                entityId: osImage.id,
                properties: updateProps
            });

            return OsImageEntity.doBulk(tenantId, osImages.map(generateOperations)).catch(
                this._logError("_bulkDelete")
            );
        }
    */

    _logError(method, desc = "Error") {
        return (error) => this._logger.warn({error}, `${this.constructor.name}.${method}: ${desc}`);
    }
    /*********************************/
    /* Start Upload Handlers */
    /*********************************/
    _onFileUploadError(osImage, error) {
        this._logError("_onFileUploadError", `Error uploading ${osImage.name}`)(error);

        osImage.setProperty("status", OS_IMAGE_STATUS.FAILED);
        return osImage
            .delete()
            .catch(this._logError("_onFileUploadError", `Error deleting ${osImage.name}`));
    }
    _onFileUploadSuccess(osImageCatalog, osImage) {
        if (osImageCatalog) {
            osImageCatalog.addToOsImagesRelationship(osImage.id);
        }
        osImage.setProperty("status", OS_IMAGE_STATUS.UPLOADED);

        return Promise.all([osImage.update(), osImageCatalog && osImageCatalog.update()]).catch(
            this._logError("_onFileUploadSuccess", `Error - update osImage ${osImage.name}`)
        );
    }
    _onFileUploadProgress(idx, bytesUploaded, bytesTotal) {
        if (this._modalUserFeedback) {
            const currentPercentage = ((bytesUploaded / bytesTotal) * 100).toFixed(2);
            const filesTotal = this._data.length;
            const fileNo = idx + 1;
            const progressMsg =
                filesTotal > 1
                    ? `#${fileNo} of ${filesTotal} files, progress: ${currentPercentage} %`
                    : `${currentPercentage} %`;
            this._modalUserFeedback.showLoadingProgress(progressMsg);
        }
    }
    _onChunkUploadComplete(osImage, chunkSize, bytesAccepted /*, bytesTotal*/) {
        osImage
            .setUploaded(bytesAccepted)
            .update({skipUpdatedOn: true, skipAudit: true})
            .catch(
                this._logError("_onChunkUploadComplete", `Error update osImage ${osImage.name}`)
            );
    }
    /*************************/
    /* End Upload Handlers   */
    /*************************/

    /*********************************/
    /* Start Window Unload Handlers */
    /*********************************/
    _onWindowBeforeunload(event) {
        const message = "You have pending uploads";
        const uncompleted = this._data.filter(({osImage}) => !isComplete(osImage.status));
        if (uncompleted.length) {
            event.preventDefault();
            event.returnValue = message;
            return message;
        }
    }
    _onWindowUnload() {
        this._abortUpload();
    }
    /*********************************/
    /* End Window Upload Handlers    */
    /*********************************/
    _preUpload() {
        this._activityMonitor.stopPolling();
        this._registerWindowListeners();
        this._controlAppNav.addLogoutPrecheck(LOGOUT_CONFIRMATION_MSG);
        if (this._userSession) {
            this._userSession.on("BeforeLogout", this._abortUpload);
        }
    }
    _postUpload() {
        this._activityMonitor.startPolling();
        this._unregisterWindowListeners();
        this._controlAppNav.removeLogoutPrecheck(LOGOUT_CONFIRMATION_MSG);
        if (this._userSession) {
            this._userSession.removeListener("BeforeLogout", this._abortUpload);
        }
    }
}
module.exports = CreateAction;

CreateAction.$inject = [
    "gameEditor.activeView",
    "$teve.services.instance.User",
    "$teve.userSession",
    "$teve.controlAppNav",
    "$teve.ui.userFeedback",
    "$teve.activityMonitor",
    "$teve.entities.OsImage",
    "$teve.entities.OsImageCatalog",
    "$teve.entities.OsImageServer",
    "$teve.storageFactory",
    "$teve.logger",
    "$teve.entities.OsUpgradeInfo",
];

CreateAction.$displayDetails = {
    type: "Action",
    name: "CreateAction",
    icon: "fileserver.add_file",
    shown: true,
    title: "Add File",
    order: 3,
};
